// redux/slices/WaitlistSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

const baseUrl = () => getServerUrl().replace(/\/$/, "");

/* ===================== PRIVATE THUNKS ===================== */

export const addToWaitlist = createAsyncThunk(
  "waitlist/addToWaitlist",
  async (waitlistData, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/waitlist", waitlistData);
      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to add to waitlist"
      );
    }
  }
);

export const fetchWaitlist = createAsyncThunk(
  "waitlist/fetchWaitlist",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest("GET", `/waitlist/${uid}`);
      const data = response?.data || response;

      return Array.isArray(data) ? data : [];
    } catch (error) {
      const status = error?.response?.status;

      if (status === 404) return [];

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to fetch waitlist"
      );
    }
  }
);

export const deleteFromWaitlist = createAsyncThunk(
  "waitlist/deleteFromWaitlist",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      await apiRequest("DELETE", `/waitlist/${id}`);
      return id;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to delete from waitlist"
      );
    }
  }
);

/* ===================== PUBLIC THUNKS ===================== */

export const addToWaitlistPublic = createAsyncThunk(
  "waitlist/addToWaitlistPublic",
  async ({ uid, payload }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const { data } = await axios.post(
        `${baseUrl()}/public/${uid}/waitlist`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to add to public waitlist"
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  waitlistEntries: [],
  status: "idle",
  error: null,
};

/* ===================== SLICE ===================== */

const waitlistSlice = createSlice({
  name: "waitlist",
  initialState,
  reducers: {
    resetWaitlistStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    clearWaitlist: (state) => {
      state.waitlistEntries = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToWaitlist.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addToWaitlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        const item = action.payload?.waitlist || action.payload;
        if (item) state.waitlistEntries.push(item);
      })
      .addCase(addToWaitlist.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to add to waitlist";
      })

      .addCase(fetchWaitlist.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchWaitlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.waitlistEntries = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchWaitlist.rejected, (state, action) => {
        state.status = "failed";
        state.waitlistEntries = [];
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch waitlist";
      })

      .addCase(deleteFromWaitlist.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteFromWaitlist.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.waitlistEntries = state.waitlistEntries.filter(
          (entry) => String(entry?.id) !== String(action.payload)
        );
      })
      .addCase(deleteFromWaitlist.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to delete from waitlist";
      })

      .addCase(addToWaitlistPublic.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addToWaitlistPublic.fulfilled, (state, action) => {
        state.status = "succeeded";
        const item = action.payload?.waitlist || action.payload;
        if (item) state.waitlistEntries.push(item);
      })
      .addCase(addToWaitlistPublic.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to add to public waitlist";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const { resetWaitlistStatus, clearWaitlist } = waitlistSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectWaitlistState = (state) => state.waitlist;
export const selectWaitlistEntries = (state) =>
  state.waitlist?.waitlistEntries || [];
export const selectWaitlistStatus = (state) =>
  state.waitlist?.status || "idle";
export const selectWaitlistError = (state) =>
  state.waitlist?.error || null;

export default waitlistSlice.reducer;